/**
 * Built-in desktop app settings (which app buttons appear in the system bar).
 *
 * Stored in localStorage so both sides agree:
 *  - the shell bar (separate renderer) reads it + listens for `storage` events
 *  - the app settings modal (Servers area) writes it
 */
import { BUILTIN_APPS, type BuiltinApp } from './builtinApps';
import type { Bilingual } from './bilingual';

export interface DesktopAppSettings {
  player: boolean;
  panel: boolean;
  pos: boolean;
  services: boolean;
  work: boolean;
}

export const DESKTOP_APPS_STORAGE_KEY = 'desktop_builtin_apps';

export const DEFAULT_DESKTOP_APPS: DesktopAppSettings = {
  player: true,
  panel: true,
  pos: false,
  // Services is a hidden surface: its mini icon shows/hides the marketplace
  // view, which never appears as a tab in the strip.
  services: true,
  // Work is the same kind of surface for employees (scaffold for now).
  work: true,
};

export function readDesktopAppSettings(): DesktopAppSettings {
  try {
    const raw = localStorage.getItem(DESKTOP_APPS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_DESKTOP_APPS };
    const parsed = JSON.parse(raw) as Partial<DesktopAppSettings>;
    return {
      player: parsed.player ?? DEFAULT_DESKTOP_APPS.player,
      panel: parsed.panel ?? DEFAULT_DESKTOP_APPS.panel,
      pos: parsed.pos ?? DEFAULT_DESKTOP_APPS.pos,
      services: parsed.services ?? DEFAULT_DESKTOP_APPS.services,
      work: parsed.work ?? DEFAULT_DESKTOP_APPS.work,
    };
  } catch {
    return { ...DEFAULT_DESKTOP_APPS };
  }
}

export function writeDesktopAppSettings(settings: DesktopAppSettings): void {
  try {
    localStorage.setItem(DESKTOP_APPS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage unavailable — settings stay session-only
  }
}

/** All apps that can be shown in the bar (with descriptions for the settings UI). */
export const DESKTOP_APP_ENTRIES: Array<BuiltinApp & { description: Bilingual }> = [
  {
    ...(BUILTIN_APPS.find((app) => app.id === 'player') as BuiltinApp),
    description: { en: 'Media player in its own tab', sl: 'Predvajalnik predstavnosti v svojem zavihku' },
  },
  {
    ...(BUILTIN_APPS.find((app) => app.id === 'panel') as BuiltinApp),
    description: { en: 'Touch panel pages in their own tab', sl: 'Strani dotikalne plošče v svojem zavihku' },
  },
  {
    ...(BUILTIN_APPS.find((app) => app.id === 'pos') as BuiltinApp),
    description: { en: 'Point of sale — coming soon', sl: 'Blagajna — kmalu na voljo' },
  },
  {
    ...(BUILTIN_APPS.find((app) => app.id === 'services') as BuiltinApp),
    description: {
      en: 'Browse services and businesses on Torik Cloud',
      sl: 'Prebrskajte storitve in podjetja v Torik Cloudu',
    },
  },
  {
    ...(BUILTIN_APPS.find((app) => app.id === 'work') as BuiltinApp),
    description: {
      en: 'Employee work pages — orders, field ops (being prepared)',
      sl: 'Delovne strani za zaposlene — naročila, delo na terenu (v pripravi)',
    },
  },
];
